import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UniversalSidePanel = ({ isOpen, onClose, record, recordType }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setActiveTab('overview');
      setIsEditing(false);
      setEditedData({});
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !record) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'history', label: 'History', icon: 'Clock' },
    { id: 'notes', label: 'Notes', icon: 'MessageSquare' }
  ];

  const getRecordIcon = (type) => {
    const icons = {
      property: 'Building2',
      certificate: 'ShieldCheck',
      maintenance: 'Wrench',
      tenancy: 'Users'
    };
    return icons?.[type] || 'FileText';
  };

  const getStatusColor = (status) => {
    if (status === 'Verified') return 'var(--color-success)';
    if (status === 'Uploaded') return 'var(--color-accent)';
    if (status === 'Missing') return 'var(--color-error)';
    return 'var(--color-muted-foreground)';
  };

  const handleSaveEdit = () => {
    console.log('Saving edits:', editedData);
    setIsEditing(false);
    setEditedData({});
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedData({});
  };

  const handleFieldChange = (field, value) => {
    setEditedData({ ...editedData, [field]: value });
  };

  const handleDocumentStatusToggle = (docId, currentStatus) => {
    const statuses = ['Missing', 'Uploaded', 'Verified'];
    const currentIndex = statuses?.indexOf(currentStatus);
    const nextStatus = statuses?.[(currentIndex + 1) % statuses?.length];
    console.log(`Document ${docId} status changed to ${nextStatus}`);
  };

  const handleAddNote = () => {
    if (newNote?.trim()) {
      console.log('Adding note:', newNote);
      setNewNote('');
    }
  };

  const renderOverviewTab = () => {
    const fields = {
      property: [
        { label: 'Property ID', key: 'id', editable: false },
        { label: 'Address', key: 'address', editable: true },
        { label: 'Landlord', key: 'landlord', editable: true },
        { label: 'Tenant', key: 'tenant', editable: true },
        { label: 'Monthly Rent', key: 'monthlyRent', editable: true, prefix: '£' },
        { label: 'Status', key: 'status', editable: true },
        { label: 'Tenancy Start', key: 'tenancyStart', editable: true },
        { label: 'Tenancy End', key: 'tenancyEnd', editable: true }
      ],
      certificate: [
        { label: 'Certificate ID', key: 'id', editable: false },
        { label: 'Certificate Type', key: 'certType', editable: false },
        { label: 'Property ID', key: 'propertyId', editable: false },
        { label: 'Address', key: 'address', editable: false },
        { label: 'Issue Date', key: 'issueDate', editable: true },
        { label: 'Expiry Date', key: 'expiryDate', editable: true },
        { label: 'Contractor', key: 'contractor', editable: true }
      ],
      maintenance: [
        { label: 'Job ID', key: 'id', editable: false },
        { label: 'Title', key: 'title', editable: true },
        { label: 'Property ID', key: 'propertyId', editable: false },
        { label: 'Address', key: 'address', editable: false },
        { label: 'Trade', key: 'trade', editable: true },
        { label: 'Priority', key: 'priority', editable: true },
        { label: 'Status', key: 'status', editable: true },
        { label: 'Contractor', key: 'contractor', editable: true },
        { label: 'Cost', key: 'cost', editable: true, prefix: '£' }
      ],
      tenancy: [
        { label: 'Tenancy ID', key: 'id', editable: false },
        { label: 'Tenant Name', key: 'tenantName', editable: true },
        { label: 'Email', key: 'email', editable: true },
        { label: 'Phone', key: 'phone', editable: true },
        { label: 'Property ID', key: 'propertyId', editable: false },
        { label: 'Address', key: 'address', editable: false },
        { label: 'Move-in Date', key: 'moveInDate', editable: true },
        { label: 'Deposit Protected', key: 'depositProtected', editable: true }
      ]
    };

    const currentFields = fields?.[recordType] || [];

    return (
      <div className="space-y-4">
        {currentFields?.map((field) => (
          <div key={field?.key}>
            <label className="text-xs font-medium text-muted-foreground font-caption block mb-1">
              {field?.label}
            </label>
            {isEditing && field?.editable ? (
              <input
                type="text"
                value={editedData?.[field?.key] !== undefined ? editedData?.[field?.key] : record?.[field?.key]}
                onChange={(e) => handleFieldChange(field?.key, e?.target?.value)}
                className="input w-full"
              />
            ) : (
              <p className="text-sm text-foreground">
                {field?.prefix}{record?.[field?.key]}
              </p>
            )}
          </div>
        ))}

        <div className="flex gap-2 pt-4">
          {!isEditing ? (
            <Button
              variant="default"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => setIsEditing(true)}
            >
              Edit Details
            </Button>
          ) : (
            <>
              <Button
                variant="default"
                size="sm"
                iconName="Check"
                iconPosition="left"
                onClick={handleSaveEdit}
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                iconPosition="left"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderDocumentsTab = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">Attached Documents</h3>
          <Button variant="default" size="sm" iconName="Upload" iconPosition="left">
            Upload
          </Button>
        </div>

        {record?.documents?.length > 0 ? (
          <div className="space-y-3">
            {record?.documents?.map((doc) => (
              <div key={doc?.id} className="p-4 rounded-lg bg-input border border-border">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Icon
                      name={doc?.type === 'pdf' ? 'FileText' : 'Image'}
                      size={20}
                      color="var(--color-accent)"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{doc?.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {doc?.size} • Uploaded {doc?.uploadDate}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => handleDocumentStatusToggle(doc?.id, doc?.status)}
                        className="px-2 py-1 rounded text-xs font-medium transition-smooth"
                        style={{
                          backgroundColor: `${getStatusColor(doc?.status)}20`,
                          color: getStatusColor(doc?.status)
                        }}
                      >
                        {doc?.status}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="h-8 w-8 rounded-lg bg-surface border border-border flex items-center justify-center transition-smooth hover:bg-muted"
                      aria-label="View document"
                    >
                      <Icon name="Eye" size={16} color="var(--color-muted-foreground)" />
                    </button>
                    <button
                      className="h-8 w-8 rounded-lg bg-surface border border-border flex items-center justify-center transition-smooth hover:bg-muted"
                      aria-label="Download document"
                    >
                      <Icon name="Download" size={16} color="var(--color-muted-foreground)" />
                    </button>
                    <button
                      className="h-8 w-8 rounded-lg bg-surface border border-border flex items-center justify-center transition-smooth hover:bg-error/10"
                      aria-label="Remove document"
                    >
                      <Icon name="Trash2" size={16} color="var(--color-error)" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="FileX" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No documents attached</p>
          </div>
        )}
      </div>
    );
  };

  const renderHistoryTab = () => {
    const mockHistory = [
      { id: 1, action: 'Record created', user: 'System', timestamp: '2024-01-15 10:30' },
      { id: 2, action: 'Document uploaded', user: 'Sarah Johnson', timestamp: '2024-01-16 14:20' },
      { id: 3, action: 'Status updated', user: 'Admin', timestamp: '2024-01-20 09:15' }
    ];

    return (
      <div className="space-y-3">
        {mockHistory?.map((item) => (
          <div key={item?.id} className="flex gap-3 p-3 rounded-lg bg-input">
            <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Clock" size={16} color="var(--color-accent)" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-foreground font-medium">{item?.action}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {item?.user} • {item?.timestamp}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderNotesTab = () => {
    return (
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-muted-foreground font-caption block mb-2">
            Add New Note
          </label>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e?.target?.value)}
            placeholder="Type your note here..."
            className="input w-full min-h-[100px] resize-none"
          />
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={handleAddNote}
            className="mt-2"
          >
            Add Note
          </Button>
        </div>

        <div className="border-t border-border pt-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Previous Notes</h3>
          {record?.notes ? (
            <div className="p-3 rounded-lg bg-input">
              <p className="text-sm text-foreground">{record?.notes}</p>
              <p className="text-xs text-muted-foreground mt-2">Admin • 2024-01-15 10:30</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No notes available</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] transition-opacity duration-300"
        onClick={onClose}
        style={{ opacity: isOpen ? 1 : 0 }}
      />
      <div
        className="fixed top-0 right-0 h-full bg-surface border-l border-border z-[201] shadow-warm-2xl transition-transform duration-300 overflow-hidden flex flex-col"
        style={{
          width: window.innerWidth < 800 ? '100%' : '400px',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)'
        }}
      >
        <div className="p-6 border-b border-border flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name={getRecordIcon(recordType)} size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{record?.title || record?.id}</h2>
              <p className="text-xs text-muted-foreground">{recordType?.toUpperCase()}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
            aria-label="Close panel"
          >
            <Icon name="X" size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="border-b border-border flex-shrink-0">
          <div className="flex">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-smooth ${
                  activeTab === tab?.id
                    ? 'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span className="hidden sm:inline">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'documents' && renderDocumentsTab()}
          {activeTab === 'history' && renderHistoryTab()}
          {activeTab === 'notes' && renderNotesTab()}
        </div>
      </div>
    </>
  );
};

export default UniversalSidePanel;